
import { Card, CardContent } from '@/components/ui/card';
import { X, Image as ImageIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { convertUrlToWebP } from '@/lib/image-conversion';

interface PlacePhotosProps {
  photos: string[];
  isEditing: boolean;
  onRemovePhoto: (index: number) => void;
}

export function PlacePhotos({ photos, isEditing, onRemovePhoto }: PlacePhotosProps) {
  const [imageStatuses, setImageStatuses] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  
  // Reset image statuses when photos array changes
  useEffect(() => {
    if (photos && photos.length > 0) {
      const initialStatuses: Record<string, 'loading' | 'loaded' | 'error'> = {};
      photos.forEach(photo => {
        initialStatuses[photo] = 'loading';
      });
      setImageStatuses(initialStatuses);
    }
  }, [photos]);

  if (!photos || photos.length === 0) return null;

  const handleImageLoad = (photoUrl: string) => {
    setImageStatuses(prev => ({ ...prev, [photoUrl]: 'loaded' }));
  };

  const handleImageError = (photoUrl: string) => {
    console.error('Image failed to load:', photoUrl);
    setImageStatuses(prev => ({ ...prev, [photoUrl]: 'error' }));
  };

  // Clean up potentially malformed URLs and convert to WebP
  const cleanImageUrl = (url: string): string => {
    try {
      // Convert to WebP format when possible
      return convertUrlToWebP(url);
    } catch (e) {
      console.warn('Error cleaning URL', e);
      return url;
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {photos.map((photo, index) => {
            const cleanedPhotoUrl = cleanImageUrl(photo);
            return (
              <div key={index} className="relative group">
                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {/* Loading placeholder */}
                  {(!imageStatuses[photo] || imageStatuses[photo] === 'loading') && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <ImageIcon className="w-8 h-8 text-gray-300 animate-pulse" />
                    </div>
                  )}
                  
                  {/* Error placeholder */}
                  {imageStatuses[photo] === 'error' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                      <span className="text-sm text-gray-500 mt-2">Image unavailable</span>
                      <span className="text-xs text-gray-400 mt-1 max-w-[80%] truncate">{cleanedPhotoUrl.substring(0, 30)}...</span>
                    </div>
                  )}
                  
                  {/* Actual image with WebP support */}
                  <picture>
                    {/* WebP source - will be used by browsers that support it */}
                    <source 
                      srcSet={cleanedPhotoUrl} 
                      type="image/webp"
                    />
                    
                    {/* Fallback image for browsers that don't support WebP */}
                    <img
                      src={cleanedPhotoUrl}
                      alt={`Photo ${index + 1}`}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${
                        imageStatuses[photo] === 'loaded' ? 'opacity-100' : 'opacity-0'
                      }`}
                      onLoad={() => handleImageLoad(photo)}
                      onError={() => handleImageError(photo)}
                      loading="lazy"
                    />
                  </picture>
                </div>
                
                {isEditing && (
                  <button
                    onClick={() => onRemovePhoto(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove photo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
